/**
 * InlineSectionEditor — Composes content + variable binding + layout settings
 * inline inside the FlexibleLayout gear panel (SortableCompactRow Collapse).
 *
 * Renders three parts:
 *   1. Content editor via FlexibleSectionContentAdapter (proxy pattern)
 *      — OR V2HeaderBarSection / V2ActionBarSection for step-level sections
 *   2. V2VariableBindingSection (embedded mode) for variable-capable kinds
 *   3. SectionLayoutSettings (Size & Flex, Appearance, Spacing & Alignment)
 *
 * Uses SAccordionNestingContext.Provider to reset nesting detection so that
 * content editors can render their SAccordion wrappers inside the gear panel
 * (which is itself inside an SAccordion).
 */

import React, { useCallback, useMemo } from 'react';
import { SAccordionNestingContext } from '@modules/ui-components';
import { FlexibleSectionContentAdapter, buildBindingProxyStep } from './FlexibleSectionContentAdapter';
import { V2VariableBindingSection } from './V2VariableBindingSection';
import { V2HeaderBarSection } from './V2HeaderBarSection';
import { V2ActionBarSection } from './V2ActionBarSection';
import { SectionLayoutSettings } from './SectionLayoutSettings';
import { ArticleIndexSectionEditor } from './ArticleIndexSectionEditor';
import type { LayoutSection, SectionLayout } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument } from '@segmently/schema-models/v2';

// Section kinds that support variable binding
const VARIABLE_CAPABLE_KINDS = new Set([
  'SingleSelectionList',
  'MultipleSelectionList',
  'OptionsList',
  'TextField',
]);

// Section kinds that have content editors (handled by FlexibleSectionContentAdapter).
const CONTENT_EDITOR_KINDS = new Set([
  'Text',
  'Media',
  'OptionsList',
  'SingleSelectionList',
  'MultipleSelectionList',
  'BulletList',
  'CustomEmbed',
  'ProductCatalog',
  'PaywallHeader',
  'TextField',
  'Button',
]);

// Article-only kind with its own settings panel (the proxy adapter has no mapping for it).
const ARTICLE_INDEX_KIND = 'ArticleIndex';

// Section kinds that write to step-level content (not section content fields).
// Rendered inline via their native V2 section components, bypassing the proxy adapter.
const STEP_LEVEL_CONTENT_KINDS = new Set(['Header', 'Footer']);

export interface InlineSectionEditorProps {
  section: LayoutSection;
  sections: LayoutSection[];
  writeSections: (updated: LayoutSection[]) => void;
  onLayoutChange: (sectionId: string, layout: SectionLayout) => void;
  locale: string;
  baseStep: FlowStepNode;
  disabled?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  availableLocales?: string[];
  flowDocument?: FlowDocument | null;
  /** Step-level onChange for screen-level section kinds (Header, Footer) */
  onContentChange?: (path: string, value: unknown) => void;
}

export const InlineSectionEditor: React.FC<InlineSectionEditorProps> = ({
  section,
  sections,
  writeSections,
  onLayoutChange,
  locale,
  baseStep,
  disabled = false,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  onContentChange,
}) => {
  const kind = section.kind as string;
  const hasContentEditor = CONTENT_EDITOR_KINDS.has(kind);
  const isStepLevelContent = STEP_LEVEL_CONTENT_KINDS.has(kind);
  const hasVariableBinding = VARIABLE_CAPABLE_KINDS.has(kind);

  // Screen-level section defaults (v2.19.0) — used only for the inherits hint
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const screenDefaultLayout = (baseStep.content as any)?.flexibleLayout
    ?.defaultSectionLayout as SectionLayout | undefined;

  // ── Variable binding proxy step ──────────────────────────────────────
  // Proxy keeps baseStep.id (screen ID) as entityId for correct boundScreenIds,
  // but overrides screenType + content so V2VariableBindingSection shows mapping UI.
  // The section.id is passed separately via sectionId prop → saved as boundSectionId.
  const proxyStepForBinding = useMemo((): FlowStepNode => {
    if (!hasVariableBinding) return baseStep;
    return buildBindingProxyStep(baseStep, section);
  }, [baseStep, section, hasVariableBinding]);

  // Variable binding writes directly to FunnelVariable via VariableEditorContext,
  // not through step.content path-based onChange. This is a no-op pass-through.
  const bindingOnChange = useCallback(
    (_path: string, _value: unknown) => { /* no-op */ },
    [],
  );

  return (
    <>
      {/* Layout settings (Size & Flex, Appearance, Spacing & Alignment) — shown first, collapsed */}
      <SectionLayoutSettings
        section={section}
        onLayoutChange={(layout) => onLayoutChange(section.id, layout)}
        disabled={disabled}
        locale={locale}
        projectId={projectId}
        onboardingId={onboardingId}
        versionId={versionId}
        availableLocales={availableLocales}
        screenDefaultLayout={screenDefaultLayout}
      />

      {/* Step-level content editors (Header → V2HeaderBarSection, Footer → V2ActionBarSection) */}
      {isStepLevelContent && onContentChange && (
        <SAccordionNestingContext.Provider value={false}>
          {kind === 'Header' && (
            <V2HeaderBarSection
              step={baseStep}
              locale={locale}
              onChange={onContentChange}
              disabled={disabled}
              defaultExpanded={true}
              sectionTitle={section.label || undefined}
              projectId={projectId}
              onboardingId={onboardingId}
              versionId={versionId}
            />
          )}
          {kind === 'Footer' && (
            <V2ActionBarSection
              step={baseStep}
              locale={locale}
              onChange={onContentChange}
              disabled={disabled}
              defaultExpanded={true}
              sectionTitle={section.label || undefined}
              projectId={projectId}
              onboardingId={onboardingId}
              versionId={versionId}
            />
          )}
        </SAccordionNestingContext.Provider>
      )}

      {/* Content editor — wrapped in nesting context reset so SAccordions render cleanly */}
      {hasContentEditor && (
        <SAccordionNestingContext.Provider value={false}>
          <FlexibleSectionContentAdapter
            section={section}
            sections={sections}
            writeSections={writeSections}
            locale={locale}
            baseStep={baseStep}
            disabled={disabled}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
          />
        </SAccordionNestingContext.Provider>
      )}

      {/* ArticleIndex (article documents only) — its own settings panel */}
      {kind === ARTICLE_INDEX_KIND && (
        <ArticleIndexSectionEditor
          section={section}
          sections={sections}
          writeSections={writeSections}
          locale={locale}
          projectId={projectId}
          disabled={disabled}
        />
      )}

      {/* Variable binding for applicable section kinds */}
      {hasVariableBinding && (
        <V2VariableBindingSection
          step={proxyStepForBinding}
          locale={locale}
          onChange={bindingOnChange}
          disabled={disabled}
          defaultExpanded={false}
          mode="embedded"
          sectionId={section.id}
        />
      )}
    </>
  );
};
